import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminLayout from '../../../components/AdminLayout';
import { LoadingState, ConfirmModal, StatsCard } from '../../../components/admin/shared';
import {
  User, Mail, Phone, MapPin, Briefcase, Calendar, Shield, Edit2, Trash2, Key, UserX, UserCheck,
  FileText, Clock, Bell
} from 'lucide-react';
import api from '../../../api/educentialApi';

const UserDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [userDetail, setUserDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({});

  // Modals
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [newRole, setNewRole] = useState('');

  useEffect(() => {
    fetchUserDetail();
  }, [id]);

  const fetchUserDetail = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/admin/users/${id}`);
      setUserDetail(response.data);
      setFormData({
        fullName: response.data.user.fullName || '',
        phone: response.data.user.phone || '',
        city: response.data.user.city || '',
        country: response.data.user.country || '',
        bio: response.data.user.bio || '',
        occupation: response.data.user.occupation || ''
      });
      setNewRole(response.data.user.role);
    } catch (error) {
      console.error('Error fetching user detail:', error);
      alert('Failed to load user details');
      navigate('/admin/users');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateUser = async () => {
    try {
      await api.patch(`/admin/users/${id}`, formData);
      setEditMode(false);
      fetchUserDetail();
      alert('User updated successfully');
    } catch (error) {
      console.error('Error updating user:', error);
      alert('Failed to update user');
    }
  };

  const handleChangeRole = async () => {
    try {
      await api.patch(`/admin/users/${id}/role`, { role: newRole });
      setShowRoleModal(false);
      fetchUserDetail();
      alert('User role updated successfully');
    } catch (error) {
      console.error('Error changing role:', error);
      alert('Failed to change user role');
    }
  };

  const handleToggleStatus = async () => {
    try {
      const newStatus = !userDetail.user.applicationStatus;
      await api.patch(`/admin/users/${id}/status`, { status: newStatus });
      fetchUserDetail();
      alert(`User ${newStatus ? 'activated' : 'deactivated'} successfully`);
    } catch (error) {
      console.error('Error toggling status:', error);
      alert('Failed to update user status');
    }
  };

  const handleResetPassword = async () => {
    try {
      const newPassword = prompt('Enter new password (min 8 characters):');
      if (!newPassword || newPassword.length < 8) {
        alert('Password must be at least 8 characters');
        return;
      }

      await api.post(`/admin/users/${id}/reset-password`, { password: newPassword });
      setShowResetPasswordModal(false);
      alert('Password reset successfully');
    } catch (error) {
      console.error('Error resetting password:', error);
      alert('Failed to reset password');
    }
  };

  const handleDeleteUser = async () => {
    try {
      await api.delete(`/admin/users/${id}`);
      alert('User deleted successfully');
      navigate('/admin/users');
    } catch (error) {
      console.error('Error deleting user:', error);
      alert('Failed to delete user');
    }
  };

  if (loading) {
    return (
      <AdminLayout title="User Details">
        <LoadingState message="Loading user details..." />
      </AdminLayout>
    );
  }

  if (!userDetail) {
    return (
      <AdminLayout title="User Details">
        <div className="p-6">
          <div className="text-center">
            <p className="text-gray-500">User not found</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  const { user, stats, recentActivity } = userDetail;

  return (
    <AdminLayout title="User Details">
      <div className="p-6 space-y-6">
        {/* Header Section */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-start justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-20 h-20 bg-gray-200 rounded-full flex items-center justify-center">
                <User className="w-10 h-10 text-gray-400" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">{user.fullName || 'No Name'}</h2>
                <p className="text-gray-500">{user.email}</p>
                <div className="flex items-center space-x-2 mt-2">
                  <span className={`px-3 py-1 text-sm font-medium rounded-full ${
                    user.role === 'ADMIN' ? 'bg-purple-100 text-purple-800' :
                    user.role === 'EDUCATOR' ? 'bg-blue-100 text-blue-800' :
                    'bg-green-100 text-green-800'
                  }`}>
                    {user.role}
                  </span>
                  <span className={`px-3 py-1 text-sm font-medium rounded-full ${
                    user.applicationStatus ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                    {user.applicationStatus ? 'Active' : 'Inactive'}
                  </span>
                  {user.emailVerified && (
                    <span className="px-3 py-1 text-sm font-medium rounded-full bg-blue-100 text-blue-800">
                      Verified
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              {editMode ? (
                <>
                  <button
                    onClick={handleUpdateUser}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    Save Changes
                  </button>
                  <button
                    onClick={() => setEditMode(false)}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                </>
              ) : (
                <button
                  onClick={() => setEditMode(true)}
                  className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  <Edit2 className="w-4 h-4" />
                  <span>Edit</span>
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <StatsCard title="Applications" value={stats.totalApplications} icon={FileText} />
          <StatsCard title="Documents" value={stats.totalDocuments} icon={FileText} />
          <StatsCard title="Notifications" value={stats.totalNotifications} icon={Bell} />
          <StatsCard
            title="Last Login"
            value={stats.lastLogin ? new Date(stats.lastLogin).toLocaleDateString() : 'Never'}
            icon={Clock}
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* User Information */}
          <div className="lg:col-span-2 space-y-6">
            {/* Profile Information */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Profile Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                  {editMode ? (
                    <input
                      type="text"
                      value={formData.fullName}
                      onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                      className="w-full border-gray-300 rounded-md"
                    />
                  ) : (
                    <p className="text-gray-900">{user.fullName || 'N/A'}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <p className="text-gray-900">{user.email}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                  {editMode ? (
                    <input
                      type="text"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="w-full border-gray-300 rounded-md"
                    />
                  ) : (
                    <p className="text-gray-900">{user.phone || 'N/A'}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                  {editMode ? (
                    <input
                      type="text"
                      value={formData.city}
                      onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                      className="w-full border-gray-300 rounded-md"
                    />
                  ) : (
                    <p className="text-gray-900">{user.city || 'N/A'}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                  {editMode ? (
                    <input
                      type="text"
                      value={formData.country}
                      onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                      className="w-full border-gray-300 rounded-md"
                    />
                  ) : (
                    <p className="text-gray-900">{user.country || 'N/A'}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Occupation</label>
                  {editMode ? (
                    <input
                      type="text"
                      value={formData.occupation}
                      onChange={(e) => setFormData({ ...formData, occupation: e.target.value })}
                      className="w-full border-gray-300 rounded-md"
                    />
                  ) : (
                    <p className="text-gray-900">{user.occupation || 'N/A'}</p>
                  )}
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Bio</label>
                  {editMode ? (
                    <textarea
                      value={formData.bio}
                      onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                      rows={3}
                      className="w-full border-gray-300 rounded-md"
                    />
                  ) : (
                    <p className="text-gray-900">{user.bio || 'N/A'}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Joined</label>
                  <p className="text-gray-900">{new Date(user.createdAt).toLocaleString()}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Last Login</label>
                  <p className="text-gray-900">
                    {user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleString() : 'Never'}
                  </p>
                </div>
              </div>
            </div>

            {/* Activity Timeline */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h3>
              <div className="space-y-4">
                {recentActivity && recentActivity.length > 0 ? (
                  recentActivity.map((activity, index) => (
                    <div key={index} className="flex items-start space-x-3 pb-4 border-b border-gray-200 last:border-0">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                        activity.type === 'application' ? 'bg-blue-100' :
                        activity.type === 'document' ? 'bg-green-100' :
                        'bg-gray-100'
                      }`}>
                        {activity.type === 'application' ? '📄' :
                         activity.type === 'document' ? '📎' : '🔔'}
                      </div>
                      <div className="flex-1">
                        <p className="text-sm text-gray-900">{activity.description}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          {new Date(activity.timestamp).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-gray-500 text-center py-4">No recent activity</p>
                )}
              </div>
            </div>
          </div>

          {/* Actions Sidebar */}
          <div className="space-y-6">
            {/* Role Management */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Role Management</h3>
              <div className="space-y-3">
                <button
                  onClick={() => setShowRoleModal(true)}
                  className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                >
                  <Shield className="w-4 h-4" />
                  <span>Change Role</span>
                </button>
              </div>
            </div>

            {/* Account Actions */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Account Actions</h3>
              <div className="space-y-3">
                <button
                  onClick={handleToggleStatus}
                  className={`w-full flex items-center justify-center space-x-2 px-4 py-2 rounded-lg ${
                    user.applicationStatus
                      ? 'bg-red-100 text-red-700 hover:bg-red-200'
                      : 'bg-green-100 text-green-700 hover:bg-green-200'
                  }`}
                >
                  {user.applicationStatus ? <UserX className="w-4 h-4" /> : <UserCheck className="w-4 h-4" />}
                  <span>{user.applicationStatus ? 'Deactivate' : 'Activate'}</span>
                </button>
                <button
                  onClick={() => setShowResetPasswordModal(true)}
                  className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200"
                >
                  <Key className="w-4 h-4" />
                  <span>Reset Password</span>
                </button>
                <button
                  onClick={() => setShowDeleteModal(true)}
                  className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200"
                >
                  <Trash2 className="w-4 h-4" />
                  <span>Delete User</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Modals */}
        <ConfirmModal
          isOpen={showDeleteModal}
          title="Delete User"
          message="Are you sure you want to delete this user? This action cannot be undone."
          confirmText="Delete"
          cancelText="Cancel"
          onConfirm={handleDeleteUser}
          onCancel={() => setShowDeleteModal(false)}
          dangerous={true}
        />

        <ConfirmModal
          isOpen={showResetPasswordModal}
          title="Reset Password"
          message="Are you sure you want to reset this user's password?"
          confirmText="Reset"
          cancelText="Cancel"
          onConfirm={handleResetPassword}
          onCancel={() => setShowResetPasswordModal(false)}
        />

        {/* Role Change Modal */}
        {showRoleModal && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen px-4">
              <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setShowRoleModal(false)}></div>
              <div className="bg-white rounded-lg overflow-hidden shadow-xl transform transition-all sm:max-w-lg sm:w-full relative z-10 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Change User Role</h3>
                <div className="space-y-3">
                  {['STUDENT', 'EDUCATOR', 'ADMIN'].map((role) => (
                    <label key={role} className="flex items-center space-x-3 cursor-pointer">
                      <input
                        type="radio"
                        name="role"
                        value={role}
                        checked={newRole === role}
                        onChange={(e) => setNewRole(e.target.value)}
                        className="text-blue-600"
                      />
                      <span className="text-gray-900">{role}</span>
                    </label>
                  ))}
                </div>
                <div className="mt-6 flex justify-end space-x-3">
                  <button
                    onClick={() => setShowRoleModal(false)}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleChangeRole}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    Change Role
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default UserDetail;
